import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { PreOrderFetchAPI } from "../api/PaymentPost/PreOrder";
import AlertSmall from "../components/Alerts/AlertSmall";
import ErrorAlert from "../components/Alerts/ErrorAlert";
import LoadingAlert from "../components/Alerts/LoadingAlert";
import { OrdersFetchAPI } from "../api/Orders";
import { BranchOfficeFetchAPI } from "../api/BranchOffices";
import { buildCloudinaryImageUrl } from "./CloudinaryImageUrl";
import { useTableSession } from "../context/TableSessionContext";


/* ─────────────────────────────────────────────────────────────
   MINI ICONS (sin dependencias externas)
───────────────────────────────────────────────────────────── */
const IcoCart = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></svg>;
const IcoX = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>;
const IcoArrow = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>;


/* ─────────────────────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────────────────────── */
const BtnShoppingcartFloat = ({
    datashoppingcart,
    currency = "USD",
    quoteData,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { hasTableSession, sharedOrder } = useTableSession();

    /* ── state ── */
    const [shoppingcart, setShoppingCart] = useState([]);
    const [totalItems, setTotalItems] = useState(() => {
        const cart = JSON.parse(localStorage.getItem("shoppingCart") || "[]");
        return cart.reduce((acc, item) => acc + (item.amount || 1), 0);
    });

    const [clientData] = useState(() => {
        const c = localStorage.getItem("client");
        return c ? JSON.parse(c) : null;
    });

    const [guestClient] = useState(() => localStorage.getItem("guestClient") || null);

    const [isOpen, setIsOpen] = useState(false);
    const [quote, setQuote] = useState(false);
    const [invoiceTip] = useState(0);
    const [totaltopay, settotaltopay] = useState(0);
    const [invoiceTotal, setInvoiceTotal] = useState(0);
    const [invoiceTotalItbms, setInvoiceTotalItbms] = useState(0);
    const [invoiceSubtotal, setInvoiceSubtotal] = useState(0);
    const [invoiceTotalDiscount, setInvoiceTotalDiscount] = useState(0);
    const [invoiceDeliveryFee, setInvoiceDeliveryFee] = useState(0);
    const [invoiceTaxDeliveryFee, setInvoiceTaxDeliveryFee] = useState(0);
    const [taxesArrayAll, setTaxesArrayAll] = useState([]);
    const [handlerEvent, setHandlerEvent] = useState(0);
    const [detailsQuote, setDetailsQuote] = useState(null);
    const [viewQuote, setViewQuote] = useState(false);
    const [bump, setBump] = useState(false);

    const overlayRef = useRef(null);
    // Una linea CANCELLED (ej. el mesero la elimino desde "Cuenta pre-pago" en vez de cobrarla) ya no
    // cuenta - sharedOrder.totals ya la excluye del lado del servidor, esto hace lo mismo aqui para que
    // no se siga viendo en el drawer del carrito con su precio y checkbox de siempre.
    const sharedCartItems = [
        ...(sharedOrder?.groups || []),
        ...(sharedOrder?.combos || []),
    ].filter((item) => item.line_status !== "CANCELLED");
    const visibleCartItems = hasTableSession ? sharedCartItems : shoppingcart;
    // En modo mesa los totales vienen del snapshot compartido (sharedOrder.totals), ya con descuentos
    // aprobados e impuestos recalculados por el servidor.
    const tableTotals = sharedOrder?.totals || {};
    const visibleItemCount = hasTableSession
        ? sharedCartItems.reduce((sum, item) => sum + Number(item.amount ?? item.qty ?? 1), 0)
        : totalItems;

    /* ── helpers (igual al original) ── */
    const money = (n) =>
        new Intl.NumberFormat("es-PA", {
            style: "currency",
            currency,
            minimumFractionDigits: 2,
        }).format(n);

    const toggle = () => setIsOpen((v) => !v);

    /* ── trigger con bump animation ── */
    const handleTrigger = () => {
        setBump(true);
        setTimeout(() => setBump(false), 400);
        toggle();
        if (!hasTableSession) getShoppingCartFromLocalStorage();
        setQuote(false);
    };

    /* ── totalItems sync ── */
    useEffect(() => {
        if (shoppingcart !== undefined) {
            setTotalItems(shoppingcart.reduce((acc, item) => acc + (item.amount || 1), 0));
        }
    }, [shoppingcart]);

    /* ── body scroll lock ── */
    useEffect(() => {
        const { style } = document.body;
        if (isOpen) {
            const prev = style.overflow;
            style.overflow = "hidden";
            return () => { style.overflow = prev; };
        }
    }, [isOpen]);

    /* ── ESC to close ── */
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") setIsOpen(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    /* ── sync con datashoppingcart prop ── */
    useEffect(() => { getShoppingCartFromLocalStorage(); }, [datashoppingcart]);

    /* ── quoteData trigger ── */
    useEffect(() => {
        if (quoteData) {
            toggle();
            getShoppingCartFromLocalStorage();
            setQuote(true);
        }
    }, [quoteData]);

    /* ── rounding helper (igual al original) ── */
    function roundUpToTwoDecimals(value) {
        const decimals = value.toString().split(".")[1];
        if (decimals && decimals.length > 2 && parseInt(decimals[2]) > 0) {
            return Math.ceil(value * 100) / 100;
        }
        return parseFloat(value.toFixed(2));
    }

    /* ── calculateInvoiceTotals (igual al original) ── */
    const calculateInvoiceTotals = async (products, combos, deliveryOption) => {
        let taxesArray = [];
        let invoiceTotalDiscount = 0;
        let invoiceSubtotal = 0;
        let invoiceTotalItbms = 0;
        let invoiceTotal = 0;

        if ((products.length + combos.length) === 0) {
            setInvoiceTotalDiscount(0);
            setInvoiceSubtotal(0);
            setInvoiceTotalItbms(0);
            setInvoiceTotal(0);
            setInvoiceDeliveryFee(0);
            setInvoiceTaxDeliveryFee(0);
            setTaxesArrayAll([]);
        } else {
            [...combos, ...products].forEach((item) => {
                let total_product_itbms = 0;
                const product_total_discount = item.unit_discount * item.amount;
                const total_product_price = item.amount * (item.price - item.unit_discount);

                item.taxes.forEach((tax) => {
                    const valueTax = roundUpToTwoDecimals(
                        item.amount * ((item.price - item.unit_discount) * tax.taxtValue)
                    );
                    tax.total = valueTax;
                    const index = taxesArray.findIndex((t) => t.tax_id === tax.tax_id);
                    if (index < 0) {
                        taxesArray.push({
                            dTasaITBMSValue: tax.dTasaITBMSValue,
                            percentage: tax.percentage,
                            tax: tax.tax,
                            tax_id: tax.tax_id,
                            taxtValue: tax.taxtValue,
                            total: valueTax,
                        });
                    } else {
                        taxesArray[index].total += valueTax;
                    }
                    total_product_itbms += valueTax;
                });

                invoiceTotalDiscount += product_total_discount;
                invoiceSubtotal += total_product_price;
                invoiceTotalItbms += total_product_itbms;
                invoiceTotal += total_product_price + total_product_itbms;
            });

            setInvoiceTotalDiscount(roundUpToTwoDecimals(invoiceTotalDiscount));
            setInvoiceSubtotal(roundUpToTwoDecimals(invoiceSubtotal));
            setInvoiceTotalItbms(roundUpToTwoDecimals(invoiceTotalItbms));
            setInvoiceTotal(roundUpToTwoDecimals(invoiceTotal));
            setTaxesArrayAll(taxesArray);
            settotaltopay(roundUpToTwoDecimals(invoiceTotal + invoiceTip));
        }
        setHandlerEvent(1);
    };

    /* ── getShoppingCartFromLocalStorage (igual al original) ── */
    const getShoppingCartFromLocalStorage = () => {
        const cart = JSON.parse(localStorage.getItem("shoppingCart") || "[]");
        setShoppingCart(cart);
        calculateInvoiceTotals(cart, [], null);
    };

    /* ── cart mutations (igual al original) ── */
    const deleteItemShop = (index) => {
        shoppingcart.splice(index, 1);
        setShoppingCart([...shoppingcart]);
        localStorage.setItem("shoppingCart", JSON.stringify(shoppingcart));
        calculateInvoiceTotals(shoppingcart, [], null);
    };

    const incrementProductAmount = (index) => {
        const product = shoppingcart[index];
        product.amount += 1;
        product.cantidadItem = product.amount;
        product.descuento += product.unit_discount;
        product.discount += product.unit_discount;
        localStorage.setItem("shoppingCart", JSON.stringify(shoppingcart));
        calculateInvoiceTotals(shoppingcart, [], null);
    };

    const decrementProductAmount = (index) => {
        const product = shoppingcart[index];
        product.amount -= 1;
        product.cantidadItem = product.amount;
        product.descuento -= product.unit_discount;
        product.discount -= product.unit_discount;
        if (product.amount <= 0) {
            shoppingcart.splice(index, 1);
            setShoppingCart([...shoppingcart]);
        }
        localStorage.setItem("shoppingCart", JSON.stringify(shoppingcart));
        calculateInvoiceTotals(shoppingcart, [], null);
    };

    /* ── branch / checkout (igual al original) ── */
    const getbranch = async () => {
        const branchoffice = JSON.parse(localStorage.getItem("branchoffice"));
        const business = JSON.parse(localStorage.getItem("business"));
        try {
            const res = await BranchOfficeFetchAPI.getBranchOfficeByCode(business.code, branchoffice.code);
            if (res.data.branchoffice.branchoffice_open === 1) {
                navigate("/cart");
            } else {
                ErrorAlert(t('alerts.attention'), t('alerts.storeClosed'), "warning");
            }
        } catch (err) {
            console.log(err);
            ErrorAlert(t('alerts.attention'), t('alerts.storeError'), "error");
        }
    };

    /* ── preOrder (igual al original) ── */
    const createPreOrder = async () => {
        if (shoppingcart.length > 0) {
            LoadingAlert();
            const token = undefined;
            const branchoffice = JSON.parse(localStorage.getItem("branchoffice"));
            const machine = JSON.parse(localStorage.getItem("machine"));
            const json = {
                client_name: clientData?.name || "Cliente generico",
                localizador: "00", client_phone: "0", ruc: "0000", dv: "00",
                client_id: clientData?.id || 0, dgi_qr: "0",
                Orden: shoppingcart, combos: [],
                Descuento: invoiceTotalDiscount, Total: invoiceTotal,
                itbms: invoiceTotalItbms, taxes: taxesArrayAll,
                subtotal: invoiceSubtotal,
                machine_id: machine.id, branchoffice_id: branchoffice.id,
                tipoOrden: "para llevar", paymethod: 0,
                is_discount_notification: false, is_cupon: false,
                is_devolution_notification: false, is_pre_order: true,
                user: { id: 0, name: "", code: 0 },
            };
            try {
                const response = await PreOrderFetchAPI.createPreOrder(json, token);
                AlertSmall(t('alerts.quoteCreated'), "success");
                localStorage.setItem("quoteData", JSON.stringify(response.data.newTempOrder));
                getTemporalOrderByCode(response.data.newTempOrder.code);
                navigate("/orders");
            } catch (err) {
                ErrorAlert(t('alerts.quoteCreateError'), "error");
                console.log(err);
            }
        } else {
            ErrorAlert(t('alerts.addSomethingToCart'), t('alerts.noProductsInCart'), "info");
        }
    };

    const getTemporalOrderByCode = async (code) => {
        try {
            const token = undefined;
            const branchoffice = JSON.parse(localStorage.getItem("branchoffice"));
            const business = JSON.parse(localStorage.getItem("business"));
            const response = await OrdersFetchAPI.getTempOrderByCode(
                code, business.code, branchoffice.code, token
            );
            setDetailsQuote(response.data.order);
            setViewQuote(true);
        } catch (err) {
            console.log(err);
        }
    };

    const validateGuestClient = () => {
        if (parseInt(guestClient) !== 0) {
            navigate("/profile");
        } else {
            createPreOrder();
        }
    };


    const location = useLocation();
    /* ══════════════════════════════════════════════════════════
       RENDER
    ══════════════════════════════════════════════════════════ */
    return (
        <>
            {/* ── TRIGGER BUTTON ── */}
            <button
                className={`position-relative co-trigger${bump ? " co-bump" : ""}`}
                onClick={handleTrigger}
                aria-label={t('cart.openAria', { count: visibleItemCount })}
            >
                <IcoCart />
                {/* {totalItems > 0 && (
                    <span style={{ fontWeight: 600 }}>{totalItems} Items</span>
                )} */}
                <span className=" d-none d-lg-inline d-md-inline" style={{ fontWeight: 400, opacity: .75, fontSize: ".72rem" }}>
                    {t('nav.cart')}
                </span>
                <div class="position-absolute top-0 start-100 translate-middle">       <span className="co-trigger-bubble">{visibleItemCount}</span></div>

            </button>

            {/* ── OVERLAY ── */}
            {isOpen && createPortal(
                <div
                    className="co-overlay"
                    ref={overlayRef}
                    onClick={(e) => { if (e.target === overlayRef.current) setIsOpen(false); }}
                />,
                document.body
            )}

            {/* ── PANEL ── */}
            {isOpen && createPortal(
                <div className="co-panel" role="dialog" aria-modal="true" aria-label={t('nav.cart')}>

                    {/* HEADER */}
                    <div className="co-header">
                        <div className="co-header-icon">
                            <IcoCart />
                        </div>
                        <div className="co-title">
                            {t('cart.yourCart')}
                            <span className="co-title-sub">
                                {hasTableSession ? `Pedido de mesa · ${visibleItemCount} productos` : t('cart.itemsCount', { count: visibleItemCount })}
                            </span>
                        </div>
                        <button
                            className="co-close"
                            onClick={() => setIsOpen(false)}
                            aria-label={t('common.close')}
                        >
                            <IcoX />
                        </button>
                    </div>

                    {/* SCROLL BODY */}
                    <div className="co-body">
                        {visibleCartItems.length === 0 ? (
                            /* EMPTY STATE */
                            <div className="co-empty">
                                <div className="co-empty-icon">
                                    <i className="bi bi-bag-x" />
                                </div>
                                <p className="co-empty-title">{t('cart.empty')}</p>
                                <p className="co-empty-sub">{t('cart.emptySubtitle')}</p>
                            </div>
                        ) : (
                            /* ITEMS — misma función viewCartItems del original, con nuevo CSS */
                            visibleCartItems.map((it, index) => (
                                <div
                                    className="co-item"
                                    key={it.line_uuid ?? it.group_id ?? it.combo_id ?? index}
                                    style={{ animationDelay: `${index * 0.04}s` }}
                                >
                                    {/* thumb */}
                                    {it.img
                                        ?
                                        <>
                                            {/* <img className="co-thumb" src={it.img} alt={it.name} loading="lazy" /> */}
                                            <img className="co-thumb border" loading="lazy"
                                                src={buildCloudinaryImageUrl({ url: it.img, width: 300, quality: "auto", type: "image" })}
                                                onError={e => e.target.style.opacity = 0}
                                            />
                                        </>


                                        : <div className="co-thumb-ph"><i className="bi bi-image" /></div>
                                    }

                                    {/* body */}
                                    <div className="co-item-body">
                                        <div className="co-item-top">
                                            <span className="co-item-name" title={it.name || it.group_name || it.combo_name}>{it.name || it.group_name || it.combo_name}</span>
                                            {!hasTableSession && <button
                                                className="co-item-del"
                                                onClick={() => deleteItemShop(index)}
                                                title={t('common.remove')}
                                            >
                                                <i className="bi bi-trash" />
                                            </button>}
                                        </div>

                                        {it.description && (
                                            <span className="co-item-desc">{it.description}</span>
                                        )}

                                        {hasTableSession && it.discount_request?.status === "APPROVED" ? (
                                            <span className="co-item-desc" style={{ color: "#0B7A44", fontWeight: 600 }}>
                                                Descuento{it.discount_request.discount_name ? ` ${it.discount_request.discount_name}` : ""}
                                                {it.discount_request.rate ? ` (${it.discount_request.rate}%)` : ""}
                                                {Number(it.discount) > 0 ? `: −${money(it.discount)}` : ""}
                                            </span>
                                        ) : hasTableSession && it.discount_request?.status === "PENDING" ? (
                                            <span className="co-item-desc" style={{ color: "#B45309", fontWeight: 600 }}>
                                                Descuento solicitado · en revisión
                                            </span>
                                        ) : null}

                                        <div className="co-controls">
                                            {/* qty stepper */}
                                            {!hasTableSession ? <div className="co-qty">
                                                <button
                                                    className="co-qty-btn"
                                                    onClick={() => decrementProductAmount(index)}
                                                >−</button>
                                                <span className="co-qty-num">{it.amount}</span>
                                                <button
                                                    className="co-qty-btn"
                                                    onClick={() => incrementProductAmount(index)}
                                                >+</button>
                                            </div> : <span>{it.amount ?? it.qty ?? 1} · {
                                                // Estado legible (antes salia el enum crudo, ej. "SUBMITTED"): igual que en "Tu pedido".
                                                it.payment_status === "PAID"
                                                    ? t('checkout.paidStatus')
                                                    : it.kitchen_status === "COOKING"
                                                        ? t('checkout.sentToKitchen')
                                                        : t('checkout.pendingStatus')
                                            }</span>}

                                            {/* price */}
                                            {hasTableSession && it.total !== undefined
                                                ? <span className="co-item-price">{money(it.total)}</span>
                                                : (it.price !== undefined ? <span className="co-item-price">${Number(it.price).toFixed(2)}</span> : null)}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                        <div style={{ height: ".5rem" }} />
                    </div>

                    {/* FOOTER FIJO — solo si hay items */}
                    {visibleCartItems.length > 0 && (
                        <div className="co-footer">

                            {/* totals — mismos datos de invoiceSubtotal, etc. */}
                            <div className="co-totals">
                                <div className="co-row">
                                    <span className="co-row-label">{t('cart.subtotal')}</span>
                                    <span className="co-row-value">{money(hasTableSession ? (tableTotals.subtotal ?? 0) : invoiceSubtotal)}</span>
                                </div>

                                {hasTableSession && Number(tableTotals.discount) > 0 && (
                                    <div className="co-row">
                                        <span className="co-row-label">Descuento</span>
                                        <span className="co-row-value" style={{ color: "#0B7A44" }}>−{money(tableTotals.discount)}</span>
                                    </div>
                                )}

                                {(hasTableSession ? Number(tableTotals.tax) : invoiceTotalItbms) > 0 && (
                                    <div className="co-row">
                                        <span className="co-row-label">{t('cart.tax')}</span>
                                        <span className="co-row-value">{money(hasTableSession ? tableTotals.tax : invoiceTotalItbms)}</span>
                                    </div>
                                )}

                                <div className="co-divider" />

                                <div className="co-total-row">
                                    <span className="co-total-label">{t('cart.total')}</span>
                                    <span className="co-total-value">{money(hasTableSession ? (tableTotals.total ?? 0) : invoiceTotal)}</span>
                                </div>
                            </div>

                            {/* CTA */}
                            <div className="d-flex justify-content-between gap-2">


                                {location.pathname === "/product" && (
                                    <button className="co-cta bg-primary-subtle w-25 text-primary-emphasis "
                                        onClick={() => { navigate("/menu") }}

                                    >
                                        +
                                    </button>
                                )}


                                <button className="co-cta" onClick={() => hasTableSession ? navigate("/cart") : getbranch()}>
                                    <i className="bi bi-receipt" />
                                    {t('cart.checkout')}
                                    <IcoArrow />
                                </button>
                            </div>
                        </div>
                    )}

                </div>,
                document.body
            )}
        </>
    );
};

export default BtnShoppingcartFloat;
